import Header from "../../shared/app-shell/Header";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import { themeAtom } from "../../shared/atoms";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { HoldCleaningInformationFilter } from "../components/HoldCleaningInformationFilter";
import { formatHoldCleaningDate, HoldCleaningMockBanner, HoldCleaningPageState } from "../components/HoldCleaningUi";
import type {
  HoldCleaningVoyagePage,
  HoldCleaningVoyageQuery,
  HoldCleaningVoyageSummary,
} from "../models/hold-cleaning.models";
import { getHoldCleaningErrorMessage, listHoldCleaningVoyages } from "../services/hold-cleaning.service";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

const PAGE_SIZE = 20;
const PHASE_TOOLTIP_ID = "hold-cleaning-voyage-phase-tooltip";
type SortBy = NonNullable<HoldCleaningVoyageQuery["sortBy"]>;

type CleaningPhase = "all" | "planning" | "transit";

const planningStatuses = ["SCHEDULED", "PLANNING", "PLANNED", "FIXED"];
const transitStatuses = ["COMMENCED", "ACTIVE", "IN_PROGRESS", "IN PROGRESS"];

const normalizedStatus = (status: string) => status.trim().toUpperCase().replaceAll("-", "_");

const voyagePhase = (voyage: HoldCleaningVoyageSummary): Exclude<CleaningPhase, "all"> | "other" => {
  const status = normalizedStatus(voyage.status);
  if (planningStatuses.some((candidate) => status.includes(candidate))) return "planning";
  if (transitStatuses.some((candidate) => status.includes(candidate))) return "transit";
  return "other";
};

const cleaningStatus = (voyage: HoldCleaningVoyageSummary) => {
  if (voyage.cleaningStatus) {
    return {
      label: voyage.cleaningStatus.label,
      className:
        voyage.cleaningStatus.implementationStatus === "NOT_IMPLEMENTED"
          ? "bg-[#F3F4F4] dark:bg-gray-900 text-[#4A5655] dark:text-gray-300"
          : "bg-[#DFF2F0] dark:bg-teal-950 text-[#176B62] dark:text-teal-200",
    };
  }
  const phase = voyagePhase(voyage);
  if (phase === "planning")
    return {
      label: "Evaluation & planning",
      className: "bg-[#FEF3C7] dark:bg-amber-950 text-[#92400E] dark:text-amber-200",
    };
  if (phase === "transit")
    return {
      label: "Cleaning in transit",
      className: "bg-[#DFF2F0] dark:bg-teal-950 text-[#176B62] dark:text-teal-200",
    };
  if (["COMPLETED", "CLOSED"].some((status) => normalizedStatus(voyage.status).includes(status))) {
    return { label: "Historical record", className: "bg-[#E5E7EB] dark:bg-gray-700 text-[#4A5655] dark:text-gray-300" };
  }
  return {
    label: voyage.status
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/^./, (value) => value.toUpperCase()),
    className: "bg-[#E5E7EB] dark:bg-gray-700 text-[#4A5655] dark:text-gray-300",
  };
};

const unavailable = <span className="text-[#899996] dark:text-gray-300">-</span>;
const designedDate = (value?: string | null) => (value ? formatHoldCleaningDate(value, false) : unavailable);

const HoldCleaningVoyagesPage = () => {
  const theme = useAtomValue(themeAtom);
  const navigate = useNavigate();
  const searchInput = useRef<HTMLInputElement>(null);
  const latestRequestId = useRef(0);
  const [searchDraft, setSearchDraft] = useState("");
  const [selectionNotice, setSelectionNotice] = useState(false);
  const [query, setQuery] = useState<HoldCleaningVoyageQuery>({
    page: 1,
    pageSize: PAGE_SIZE,
    sortBy: "vessel",
    sortDirection: "asc",
  });
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>([undefined]);
  const [result, setResult] = useState<HoldCleaningVoyagePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    log("HoldCleaningVoyagesPage - load", { page: query.page });
    setLoading(true);
    setError(null);
    try {
      const page = await listHoldCleaningVoyages(query, true);
      if (requestId === latestRequestId.current) {
        log("STATE - load - hold cleaning voyages", { count: page.items.length });
        setResult(page);
      }
    } catch (requestError) {
      if (requestId === latestRequestId.current) {
        logE(new Error("HoldCleaningVoyagesPage - load - failed"));
        setError(getHoldCleaningErrorMessage(requestError));
      }
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
    return () => {
      latestRequestId.current += 1;
    };
  }, [load]);

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    log("HoldCleaningVoyagesPage - applySearch");
    setCursorHistory([undefined]);
    setResult(null);
    setQuery((current) => ({
      ...current,
      search: searchDraft.trim() || undefined,
      cursor: undefined,
      page: 1,
    }));
  };

  const phase: CleaningPhase = query.phase ?? "all";
  const visibleVoyages = result?.items ?? [];
  const matchingCount = result?.phaseCounts?.[phase];
  const firstVisible = visibleVoyages.length && result ? (result.page - 1) * PAGE_SIZE + 1 : 0;
  const lastVisible = firstVisible ? firstVisible + visibleVoyages.length - 1 : 0;

  const tabs: Array<{ id: CleaningPhase; label: string; description: string }> = [
    {
      id: "all",
      label: "All",
      description:
        "One entry per vessel with cleaning information on its current voyage. Scheduled voyages are excluded; completed voyages are used only when no commenced voyage exists.",
    },
    {
      id: "transit",
      label: "Cleaning in Transit",
      description:
        "Voyages marked commenced, active or in progress in IMOS. This does not confirm cleaning has started or the vessel is at sea.",
    },
  ];

  const openVoyage = (imo: string) => {
    log("HoldCleaningVoyagesPage - openVoyage", { imoNumber: imo });
    navigate(`/hold-cleaning/vessels/${encodeURIComponent(imo)}`);
  };
  const changeSort = (sortBy: SortBy) => {
    setCursorHistory([undefined]);
    setResult(null);
    setQuery((current) => ({
      ...current,
      sortBy,
      sortDirection: current.sortBy === sortBy && current.sortDirection === "asc" ? "desc" : "asc",
      cursor: undefined,
      page: 1,
    }));
  };
  const sortableHeader = (label: string, sortBy: SortBy, width: string) => (
    <th
      key={sortBy}
      scope="col"
      aria-sort={query.sortBy === sortBy ? (query.sortDirection === "desc" ? "descending" : "ascending") : "none"}
      className={`${width} px-4 py-3`}
    >
      <button
        type="button"
        onClick={() => changeSort(sortBy)}
        className="inline-flex items-center gap-1 text-left hover:text-[#176B62] focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475] dark:hover:text-teal-200"
        title={`Sort by ${label}`}
      >
        {label}
        <span aria-hidden="true">{query.sortBy === sortBy ? (query.sortDirection === "desc" ? "↓" : "↑") : "↕"}</span>
      </button>
    </th>
  );

  logV("HoldCleaningVoyagesPage - BUILD", { loading, count: visibleVoyages.length });

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className={twMerge("flex min-w-0 flex-1 flex-col overflow-y-auto px-6", theme.home.tailwind.pageBackground)}>
        <Header />
        <main className="mx-auto flex w-full max-w-[1680px] flex-1 flex-col gap-5 px-2 pb-10 text-[#292F2D] dark:text-gray-100">
          <HoldCleaningMockBanner />
          <header>
            <h1 className="text-[30px] font-semibold leading-tight">Hold Cleaning</h1>
            <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
              Evaluate hold cleanliness, plan cleaning and track progress towards the next load port.
            </p>
          </header>

          {selectionNotice && (
            <div
              role="status"
              className="rounded-lg border border-[#9CCFC8] bg-[#E9F4F3] px-4 py-3 text-sm text-[#176B62] dark:border-teal-700 dark:bg-teal-950 dark:text-teal-200"
            >
              Search for a vessel, then select its row to start or update the evaluation. The service does not expose a
              separate create-evaluation endpoint.
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4">
            <Tooltip
              id={PHASE_TOOLTIP_ID}
              place="bottom"
              positionStrategy="fixed"
              disableStyleInjection={true}
              style={{
                backgroundColor: "#292F2D",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "12px",
                maxWidth: "320px",
                zIndex: 1000,
              }}
            />
            <div className="inline-flex overflow-hidden rounded-lg border border-[#C7D0CE] bg-white p-1 shadow-sm dark:border-gray-600 dark:bg-gray-800">
              {tabs.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  aria-describedby={`hold-cleaning-phase-${item.id}-description`}
                  data-tooltip-id={PHASE_TOOLTIP_ID}
                  data-tooltip-content={item.description}
                  disabled={!result?.phaseCounts}
                  onClick={() => {
                    if (phase === item.id) return;
                    setCursorHistory([undefined]);
                    setResult(null);
                    setQuery((current) => ({
                      ...current,
                      phase: item.id === "all" ? undefined : item.id,
                      cursor: undefined,
                      page: 1,
                    }));
                  }}
                  className={twMerge(
                    "rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                    phase === item.id
                      ? "bg-[#DFF2F0] text-[#176B62] dark:bg-teal-950 dark:text-teal-200"
                      : "text-[#4A5655] hover:bg-[#F3F4F6] dark:text-gray-300 dark:hover:bg-gray-700",
                  )}
                >
                  {item.label}
                  <span className="ml-2 rounded-full bg-white/80 px-2 py-0.5 text-xs dark:bg-gray-700">
                    {result?.phaseCounts?.[item.id] ?? "—"}
                  </span>
                  <span id={`hold-cleaning-phase-${item.id}-description`} hidden>
                    {item.description}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex flex-1 flex-wrap justify-end gap-3">
              <HoldCleaningInformationFilter
                selected={query.information ?? []}
                counts={result?.informationCounts}
                loading={loading}
                onChange={(information) => {
                  setCursorHistory([undefined]);
                  setResult(null);
                  setQuery((current) => ({
                    ...current,
                    information,
                    cursor: undefined,
                    page: 1,
                  }));
                }}
              />
              <form className="relative min-w-[280px] max-w-[440px] flex-1" onSubmit={applySearch}>
                <label className="sr-only" htmlFor="hold-cleaning-search">
                  Search hold cleaning voyages
                </label>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-[#899996] dark:text-gray-300"
                  aria-hidden="true"
                />
                <input
                  ref={searchInput}
                  id="hold-cleaning-search"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder="Search vessel, IMO, cargo or port"
                  className="w-full rounded-lg border border-[#C7D0CE] bg-white py-2.5 pl-10 pr-3 text-sm text-[#292F2D] shadow-sm outline-none focus:border-[#1F8475] focus:ring-1 focus:ring-[#1F8475] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                <button type="submit" className="sr-only">
                  Search
                </button>
              </form>
              <button
                type="button"
                onClick={() => {
                  setSelectionNotice(true);
                  searchInput.current?.focus();
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-[#1F8475] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#176B62]"
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                New evaluation
              </button>
            </div>
          </div>

          {error && !result ? (
            <HoldCleaningPageState
              title="Voyages could not be loaded"
              message={error}
              tone="error"
              action={
                <button
                  type="button"
                  className="rounded-lg border border-[#C7D0CE] bg-white px-3 py-2 text-sm font-semibold dark:border-gray-600 dark:bg-gray-800"
                  onClick={load}
                >
                  Try again
                </button>
              }
            />
          ) : loading && !result ? (
            <HoldCleaningPageState
              title="Loading vessels"
              message="Finding vessels with current-voyage cleaning records..."
            />
          ) : result ? (
            <section className="overflow-hidden rounded-lg border border-[#DFE4E2] bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {error && (
                <div className="border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-800">
                  Showing the last loaded page. Refresh failed: {error}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1480px] table-fixed text-left text-sm">
                  <thead className="sticky top-0 z-10 border-b border-[#BBDDD8] bg-[#DFF2F0] text-[11px] uppercase tracking-[0.06em] text-[#2F7F7B] dark:border-teal-800 dark:bg-teal-950 dark:text-teal-200">
                    <tr>
                      {sortableHeader("Vessel", "vessel", "w-[190px]")}
                      <th className="w-[180px] px-4 py-3">Cleaning Status</th>
                      {sortableHeader("Current / Last Cargo", "cargo", "w-[170px]")}
                      {sortableHeader("Discharging Port", "dischargePort", "w-[150px]")}
                      {sortableHeader("Discharge Completion", "dischargeCompletion", "w-[160px]")}
                      {sortableHeader("Next Cargo", "nextCargo", "w-[140px]")}
                      {sortableHeader("Loading Port", "loadPort", "w-[140px]")}
                      {sortableHeader("ETA", "eta", "w-[125px]")}
                      {sortableHeader("Laycan", "laycan", "w-[125px]")}
                      <th className="w-[130px] px-4 py-3">Latest Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8ECEB] dark:divide-gray-700">
                    {visibleVoyages.map((voyage) => {
                      const status = cleaningStatus(voyage);
                      return (
                        <tr
                          key={voyage.voyageId}
                          tabIndex={0}
                          className="cursor-pointer text-[#292F2D] transition-colors hover:bg-[#E9F4F3] focus:bg-[#E9F4F3] focus:outline-none dark:text-gray-100 dark:hover:bg-teal-950 dark:focus:bg-teal-950"
                          onClick={() => openVoyage(voyage.imo)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openVoyage(voyage.imo);
                            }
                          }}
                        >
                          <td className="px-4 py-4">
                            <p className="font-semibold">{voyage.vesselName}</p>
                            {(voyage.imageCount ?? 0) > 0 && (
                              <p className="mt-1 text-xs text-[#176B62] dark:text-teal-200">
                                {voyage.imageCount} photos on current voyage
                              </p>
                            )}
                            <p className="mt-1 text-xs text-[#899996] dark:text-gray-300">IMO {voyage.imo}</p>
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={twMerge(
                                "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                                status.className,
                              )}
                            >
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <p>{voyage.cargo ?? voyage.previousCargo ?? unavailable}</p>
                            {voyage.cargo && voyage.previousCargo && voyage.cargo !== voyage.previousCargo && (
                              <p className="mt-1 text-xs text-[#899996] dark:text-gray-300">
                                Last: {voyage.previousCargo}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-4">{voyage.dischargePort ?? unavailable}</td>
                          <td className="px-4 py-4">{formatHoldCleaningDate(voyage.completedAt, false)}</td>
                          <td className="px-4 py-4">{voyage.nextCargo ?? unavailable}</td>
                          <td className="px-4 py-4">{voyage.nextLoadPort ?? unavailable}</td>
                          <td className="px-4 py-4">{designedDate(voyage.nextEta)}</td>
                          <td className="px-4 py-4">
                            {voyage.laycanStartAt || voyage.laycanEndAt ? (
                              <>
                                {formatHoldCleaningDate(voyage.laycanStartAt, false)}
                                {voyage.laycanEndAt && ` - ${formatHoldCleaningDate(voyage.laycanEndAt, false)}`}
                              </>
                            ) : (
                              unavailable
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {voyage.latestRating?.score === null || voyage.latestRating?.score === undefined ? (
                              <span className="text-[#899996] dark:text-gray-300">—</span>
                            ) : (
                              <div>
                                <p className="font-semibold text-[#1F8475] dark:text-teal-300">
                                  {voyage.latestRating.score} / {voyage.latestRating.maximumScore}
                                </p>
                                <p className="mt-1 text-xs text-[#899996] dark:text-gray-300">
                                  {voyage.latestRating.label}
                                </p>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {visibleVoyages.length === 0 && (
                <div className="px-6 py-14 text-center">
                  <p className="font-semibold text-[#292F2D] dark:text-gray-100">No voyages in this view</p>
                  <p className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                    Change the phase, cleaning-data filter or search criteria.
                  </p>
                </div>
              )}

              <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-[#DFE4E2] px-5 py-4 text-sm text-[#4A5655] dark:border-gray-700 dark:text-gray-300">
                <span>
                  {matchingCount === undefined
                    ? `Page ${result.page} · ${visibleVoyages.length} voyages shown on this page`
                    : `Showing ${firstVisible}–${lastVisible} of ${matchingCount} matching voyages`}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rounded-lg border border-[#C7D0CE] bg-white px-3 py-2 font-semibold hover:bg-[#F3F4F6] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
                    disabled={result.page <= 1 || loading}
                    onClick={() =>
                      setQuery((current) => ({
                        ...current,
                        cursor: current.sortBy ? undefined : cursorHistory[current.page - 2],
                        page: current.page - 1,
                      }))
                    }
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-[#C7D0CE] bg-white px-3 py-2 font-semibold hover:bg-[#F3F4F6] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
                    disabled={!result.nextCursor || loading}
                    onClick={() => {
                      if (!result.nextCursor) return;
                      if (!query.sortBy)
                        setCursorHistory((current) => [
                          ...current.slice(0, result.page),
                          result.nextCursor ?? undefined,
                        ]);
                      setQuery((current) => ({
                        ...current,
                        cursor: current.sortBy ? undefined : result.nextCursor ?? undefined,
                        page: current.page + 1,
                      }));
                    }}
                  >
                    Next
                  </button>
                </div>
              </footer>
            </section>
          ) : null}
        </main>
        <PageFooter />
      </div>
    </div>
  );
};

export default HoldCleaningVoyagesPage;
